/**
 * PersonalizationBanner Component
 * Shared banner showing personalization info with dismiss and settings link.
 * Used on Connect page and Catalog pages.
 */

import { X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { RitualityIcon } from "./RitualityIcon"

const BANNER_DISMISS_KEY = "rituality_personalization_banner_dismissed"

interface PersonalizationBannerProps {
  title?: string
  description?: string
  className?: string
}

export function PersonalizationBanner({
  title = "Personalized for You",
  description = "Based on your intentions, location and activity, we've curated this selection for you. You can update preferences in your",
  className = "",
}: PersonalizationBannerProps) {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(BANNER_DISMISS_KEY) === "true"
    }
    return false
  })

  useEffect(() => {
    if (dismissed) {
      localStorage.setItem(BANNER_DISMISS_KEY, "true")
    }
  }, [dismissed])

  const handleDismiss = useCallback(() => {
    setDismissed(true)
  }, [])

  if (dismissed) return null

  return (
    <div
      className={`relative flex items-center gap-4 bg-connect-gold-bg border border-connect-gold-bg rounded-[10px] px-5 py-4 min-h-[95px] ${className}`}
    >
      {/* Rituality Icon */}
      <div className="hidden md:flex flex-shrink-0 w-[55px] h-[55px] rounded-full bg-connect-gold-icon-bg flex items-center justify-center">
        <RitualityIcon size={25} className="text-chat-link" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0 py-2 px-1">
        <p className="text-[16px] font-semibold text-text-primary mb-0.5">{title}</p>
        <p className="text-[13px] text-text-primary/80 leading-[20px]">
          {description}{" "}
          <Link to="/profile/edit" className="font-bold hover:underline">
            profile settings
          </Link>
          .
        </p>
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-5 top-5 flex-shrink-0 p-1 opacity-50 hover:opacity-80 transition-opacity cursor-pointer"
        aria-label="Dismiss banner"
      >
        <X className="w-[15px] h-[15px] text-text-primary" />
      </button>
    </div>
  )
}
